import axiosInstance, { axiosNoLogout } from "../axios";
import { getLoginToken, setLoginToken } from "../storage";
import { BaseUrl, COLLECTIONS, ROLES, EMP_ROLES } from "../config";
import { firebase } from "../firebase-config";
import validator from "validator";
import {
  query,
  where,
  getDocs,
  getCountFromServer,
  collection,
  documentId,
  getDoc,
  doc,
  setDoc,
  arrayRemove,
  arrayUnion,
  serverTimestamp,
  updateDoc,
  deleteField,
  limit,
  addDoc,
  or,
  and,
  increment,
  orderBy,
  startAfter,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
const defaultHeaders = {
  "Content-Type": "application/json",
  withCredentials: true,
};

// Reduce duplicate Stripe quantity writes caused by rapid UI actions.
const lastSyncedQuantityByItem = new Map();
const inFlightQuantitySyncByItem = new Map();

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function postStripeQuantityWithRetry(subscriptionId, subscriptionItemId, quantity) {
  const retryDelaysMs = [0, 700, 1800];
  let lastError = null;

  for (let i = 0; i < retryDelaysMs.length; i++) {
    try {
      if (retryDelaysMs[i] > 0) {
        await sleep(retryDelaysMs[i]);
      }
      const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
      await axiosNoLogout.post(
        `${BaseUrl}stripeProxyUpdateSubscriptionQuantity`,
        { subscriptionId, subscriptionItemId, quantity },
        { headers: { ...defaultHeaders, ...authHeaders } }
      );
      return;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
}

/** National digits for Firestore (matches uniqueness queries & UI). */
function nationalPhoneForStorage(phone) {
  const d = String(phone ?? "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 11 && d[0] === "1") return d.slice(1);
  if (d.length === 12 && d.startsWith("91")) return d.slice(2);
  if (d.length > 10) return d.slice(-10);
  return d;
}

/** E.164 for Firebase Auth `createUser` only (Admin SDK requirement). */
function phoneForFirebaseAuth(phone) {
  const d = String(phone ?? "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 11 && d[0] === "1") {
    const n = d.slice(1);
    if (n.length === 10 && validator.isMobilePhone(`+1${n}`, "en-US")) return `+1${n}`;
  }
  if (d.length === 10) {
    const us = `+1${d}`;
    if (validator.isMobilePhone(us, "en-US")) return us;
    const inMobile = `+91${d}`;
    if (validator.isMobilePhone(inMobile, "en-IN")) return inMobile;
    return us;
  }
  const trimmed = String(phone).trim();
  if (trimmed.startsWith("+")) return trimmed.replace(/\s/g, "");
  return `+${d}`;
}

/**
 * get list of all employees
 * @param {*} formData  : may be used for filter data by fields
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getEmployeesList(formData, onSuccess, onError) {
  try {
    const empQuery = query(
      collection(firebase, COLLECTIONS.USERS),
      or(
        where("role", "==", ROLES.EMPLOYEE),
        where("role", "array-contains", ROLES.EMPLOYEE)
      )
    );

    const querySnapshot = await getDocs(empQuery);
    // const results = [];

    // querySnapshot.forEach((doc) => {
    //   const data = doc.data();
    //   results.push({ id: doc.id, ...data });
    // });
    const employees = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    onSuccess && onSuccess(employees);
  } catch (error) {
    console.log("Error getting documents: ", error);
    onError && onError(error);
  }
}

/**
 * get list of all employees
 * @param {*} formData  : may be used for array ids
 * @param {*} onSuccess
 * @param {*} onError
 */
function chunkArray(array, size) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

export async function getEmployeesListBasedOnId(formData, onSuccess, onError) {
  try {
    if (!formData || formData.length === 0) {
      onSuccess && onSuccess(null);
      return;
    }

    const colRef = collection(firebase, COLLECTIONS.USERS);
    const chunks = chunkArray(formData, 10);

    const results = [];

    for (const chunk of chunks) {
      const empQuery = query(colRef, where(documentId(), "in", chunk));
      const querySnapshot = await getDocs(empQuery);
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        results.push({ id: doc.id, ...data });
      });
    }

    onSuccess && onSuccess(results.length ? results : null);
  } catch (error) {
    console.log("Error getting documents: ", error);
    onError && onError(error);
  }
}

/**
 * Get details of perticular employee
 * @param {*} id : id of employee
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getEmployee(id, onSuccess, onError) {
  try {
    const empDocRef = doc(firebase, COLLECTIONS.USERS, id);

    const docSnapshot = await getDoc(empDocRef);

    if (!docSnapshot.exists()) {
      onError && onError({ status: false, error: "Employee not found" });
    } else {
      const data = docSnapshot.data();
      onSuccess &&
        onSuccess({ status: true, data: { id: docSnapshot.id, ...data } });
    }
  } catch (error) {
    onError && onError({ status: false, error: error });
  }
}

export async function getCompanyEmployees(companyId, onSuccess, onError, options = {}) {
  try {
    const constraints = [
      or(
        and(where("companyId", "==", companyId), where("role", "==", "Employee")),
        and(where("companyId", "==", companyId), where("role", "array-contains", "Employee"))
      ),
    ];

    if (options.limit) {
      constraints.push(limit(options.limit));
    }

    const employeesQuery = query(
      collection(firebase, COLLECTIONS.USERS),
      ...constraints
    );

    const querySnapshot = await getDocs(employeesQuery);

    if (querySnapshot.empty) {
      onSuccess && onSuccess({ status: true, data: null });
    } else {
      const employees = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      onSuccess && onSuccess({ status: true, data: employees });
    }
  } catch (error) {
    onError && onError({ status: false, error: error.message });
  }
}

const EMP_PAGE_SIZE = 50;

/**
 * Get company employees with cursor-based pagination.
 * Filters by companyId server-side; role filter applied in memory
 * because Firestore OR composite filters do not support startAfter cursors.
 * @param {string} companyId
 * @param {Function} onSuccess - ({ status, data, lastDoc, hasMore }) => void
 * @param {Function} onError
 * @param {Object} options - { lastDoc, pageSize }
 */
export async function getCompanyEmployeesPaginated(companyId, onSuccess, onError, options = {}) {
  try {
    const pageSize = options.pageSize ?? EMP_PAGE_SIZE;
    const lastDocCursor = options.lastDoc ?? null;

    let empQuery = query(
      collection(firebase, COLLECTIONS.USERS),
      where("companyId", "==", companyId),
      orderBy("__name__"),
      limit(pageSize)
    );

    if (lastDocCursor) {
      empQuery = query(
        collection(firebase, COLLECTIONS.USERS),
        where("companyId", "==", companyId),
        orderBy("__name__"),
        startAfter(lastDocCursor),
        limit(pageSize)
      );
    }

    const querySnapshot = await getDocs(empQuery);
    const allUsers = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    const newLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] ?? null;
    const hasMore = querySnapshot.docs.length === pageSize;

    onSuccess?.({ status: true, data: allUsers, lastDoc: newLastDoc, hasMore });
  } catch (error) {
    console.error("Error fetching paginated employees:", error);
    onError?.({ status: false, error: error.message });
  }
}

export async function getCompanyUsers(companyId, onSuccess, onError) {
  try {
    const byFieldQuery = query(
      collection(firebase, COLLECTIONS.USERS),
      where("companyId", "==", companyId)
    );

    const byIdQuery = query(
      collection(firebase, COLLECTIONS.USERS),
      where(documentId(), "==", companyId)
    );

    const [fieldSnapshot, idSnapshot] = await Promise.all([
      getDocs(byFieldQuery),
      getDocs(byIdQuery),
    ]);

    const mergedMap = new Map();

    fieldSnapshot.forEach((doc) => {
      mergedMap.set(doc.id, { id: doc.id, ...doc.data() });
    });

    idSnapshot.forEach((doc) => {
      mergedMap.set(doc.id, { id: doc.id, ...doc.data() });
    });

    const mergedResults = Array.from(mergedMap.values());

    onSuccess && onSuccess({
      status: true,
      data: mergedResults.length ? mergedResults : null,
    });
  } catch (error) {
    onError && onError({
      status: false,
      error: error.message,
    });
  }
}

export async function getAllCompanyUsers() {
  try {
    const employeesQuery = query(
      collection(firebase, COLLECTIONS.USERS),
      where("role", "==", "companyUser")
    );

    const querySnapshot = await getDocs(employeesQuery);

    if (querySnapshot.empty) {
      return { status: true, data: null };
    } else {
      const employees = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { status: true, data: employees };
    }
  } catch (error) {
    return { status: false, error: error.message };
  }
}

/**
 * Get details of All employee
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getAllEmployee(onSuccess, onError) {
  try {
    const colRef = collection(firebase, COLLECTIONS.USERS);

    const querySnapshot = await getDocs(colRef);

    if (querySnapshot.empty) {
      onError && onError({ status: false, error: "No employees found" });
    } else {
      const employees = [];
      querySnapshot.forEach((doc) => {
        employees.push({ id: doc.id, ...doc.data() });
      });
      onSuccess && onSuccess({ status: true, data: employees });
    }
  } catch (error) {
    onError && onError({ status: false, error });
  }
}

export async function getAllClientsByCompany(companyId, onSuccess, onError) {
  try {
    const colRef = collection(firebase, COLLECTIONS.USERS);

    const q = query(
      colRef,
      where("companyId", "==", companyId),
      where("role", "==", "Client"),
      where("status", "==", true)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      onSuccess && onSuccess({ status: true, data: null });
    } else {
      const clients = [];
      querySnapshot.forEach((doc) => {
        clients.push({ id: doc.id, ...doc.data() });
      });
      onSuccess && onSuccess({ status: true, data: clients });
    }
  } catch (error) {
    onError && onError({ status: false, error });
  }
}

/**
 * update group collection record for employess
 * add or remove employee from perticular group
 * @param {*} type [add or remove]
 * @param {*} groupId
 * @param {*} EmployeeId
 * @returns
 */
export async function updateEmployeetoGroup(type, groupId, EmployeeId) {
  const documentRef = doc(firebase, COLLECTIONS.GROUPS, groupId);

  try {
    if (type === "add") {
      await setDoc(
        documentRef,
        {
          employeeId: arrayUnion(EmployeeId),
        },
        { merge: true }
      );
      return { status: true, message: "Updated successfully" };
    } else if (type === "remove") {
      await setDoc(
        documentRef,
        {
          employeeId: arrayRemove(EmployeeId),
        },
        { merge: true }
      );
      return { status: true, message: "Updated successfully" };
    } else {
      throw new Error("Invalid operation type");
    }
  } catch (error) {
    return { status: false, error: error.message || error };
  }
}

/**
 * Create User with employee role in users collection
 * called from addemployee function
 * @param {*} formData
 * @returns
 */
async function createEmployeeUser(formData) {
  const currentUserId = getAuth().currentUser.uid;

  try {
    const userDocRef = doc(firebase, COLLECTIONS.USERS, formData?.userId);

    await setDoc(
      userDocRef,
      {
        ...formData,
        password: deleteField(),
        createdBy: currentUserId,
        createdAt: new Date().getTime(),
        updatedAt: new Date().getTime(),
      },
      { merge: true }
    );

    const responseItem = {
      ...formData,
      id: formData?.userId,
    };
    return { status: true, data: responseItem };
  } catch (error) {
    return { status: false, error: error.message || error };
  }
}

/**
 * Update data of user
 * @param {*} id
 * @param {*} formData
 * @returns
 */
export async function updateUserPassword(id, formData) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  try {
    const response = await axiosInstance.post(
      `${BaseUrl}updateUserPass`,
      { email: formData.email, password: formData.password },
      { headers: { ...defaultHeaders, ...authHeaders } }
    );
    return {
      status: true,
      message: "Updated successfully",
      response: response.data || formData,
    };
  } catch (error) {
    console.error("Error updating user password:", error);
    return {
      status: false,
      error: error.response?.data || error.message || error,
    };
  }
}
export async function updateUser(id, formData) {
  try {
    const auth = getAuth();
    const currentUserId = auth.currentUser.uid;
    const documentRef = doc(firebase, COLLECTIONS.USERS, id);

    await setDoc(
      documentRef,
      {
        ...formData,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      },
      { merge: true }
    );

    return {
      status: true,
      message: "Updated successfully",
      response: { id, ...formData },
    };
  } catch (error) {
    console.error("Error updating user:", error);
    return { status: false, error: error.message || error };
  }
}

export async function getActiveEmployeeCount(companyId) {
  try {
    const usersRef = collection(firebase, COLLECTIONS.USERS);

    // Two parallel count queries cover both role storage formats (string vs array).
    // getCountFromServer reads zero documents — Firestore returns only the count.
    // If a composite index error appears, create the index via the Firebase Console
    // link provided in the error message.
    const [stringRoleSnap, arrayRoleSnap] = await Promise.all([
      getCountFromServer(query(
        usersRef,
        where("companyId", "==", companyId),
        where("role", "==", "Employee"),
        where("status", "==", true),
      )),
      getCountFromServer(query(
        usersRef,
        where("companyId", "==", companyId),
        where("role", "array-contains", "Employee"),
        where("status", "==", true),
      )),
    ]);

    return stringRoleSnap.data().count + arrayRoleSnap.data().count;
  } catch (error) {
    console.error("Error fetching employee count:", error);
    throw error;
  }
}

export async function getActiveStripeUserCount(companyId) {
  try {
    const usersRef = collection(firebase, COLLECTIONS.USERS);
    const q = query(
      usersRef,
      where("companyId", "==", companyId),
      where("status", "==", true)
    );
    const querySnapshot = await getDocs(q);

    let activeBilledUsers = 0;
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.deleted) return;

      const roles = Array.isArray(data.role) ? data.role : [data.role];
      const hasCompanyUser = roles.includes("companyUser") || roles.includes("superUser");

      if (!hasCompanyUser) {
        activeBilledUsers++;
      }
    });

    return activeBilledUsers;
  } catch (error) {
    console.error("Error fetching active billed user count:", error);
    return 0;
  }
}

export async function updateStripeQuantity(
  subscriptionId,
  subscriptionItemId,
  companyId
) {
  if (!subscriptionId || !subscriptionItemId || typeof subscriptionId !== "string" || typeof subscriptionItemId !== "string") {
    return;
  }
  try {
    const itemKey = `${subscriptionId}:${subscriptionItemId}`;
    const inFlightState = inFlightQuantitySyncByItem.get(itemKey);
    if (inFlightState) {
      inFlightState.needsResync = true;
      inFlightState.latestCompanyId = companyId;
      return inFlightState.promise;
    }

    const user = getAuth().currentUser;
    if (!user) return;
    const freshToken = await user.getIdToken(true);
    if (freshToken) setLoginToken(freshToken);
    const syncState = {
      latestCompanyId: companyId,
      needsResync: false,
      promise: null,
    };

    const syncPromise = (async () => {
      do {
        syncState.needsResync = false;
        const quantity = await getActiveStripeUserCount(syncState.latestCompanyId);
        const lastQuantity = lastSyncedQuantityByItem.get(itemKey);

        if (lastQuantity === quantity) {
          continue;
        }

        try {
          await postStripeQuantityWithRetry(subscriptionId, subscriptionItemId, quantity);
          lastSyncedQuantityByItem.set(itemKey, quantity);
          console.log("Stripe quantity updated to:", quantity);
        } catch (error) {
          console.error("Stripe quantity update failed after retries:", error);
          // If another user action happened while this call was retrying, loop once
          // more and sync the latest computed quantity.
          if (!syncState.needsResync) {
            break;
          }
        }
      } while (syncState.needsResync);
    })();

    syncState.promise = syncPromise;
    inFlightQuantitySyncByItem.set(itemKey, syncState);
    await syncPromise;
  } catch (error) {
    console.error("Stripe quantity update failed:", error);
  } finally {
    const itemKey = `${subscriptionId}:${subscriptionItemId}`;
    inFlightQuantitySyncByItem.delete(itemKey);
  }
}

/**
 * Add employee to user
 * First create user to firebase auth user then add employee data to users collection
 * @param {*} formData
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function addEmployee(formData, subscriptionId = null, subscriptionItemId = null) {
  try {
    const phoneNational = nationalPhoneForStorage(formData.phoneNumber);
    const phoneAuth = phoneForFirebaseAuth(formData.phoneNumber);

    const NewUserData = {
      firstname: formData.firstName,
      lastname: formData.lastName,
      email: formData.email,
      phoneNumber: phoneAuth,
      password: formData.password,
      companyId: formData.companyId,
    };

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const CreatedUser = await axiosInstance.post(
      `${BaseUrl}createUser`,
      NewUserData,
      { headers: { ...defaultHeaders, ...authHeaders } }
    );

    if (CreatedUser?.data?.status !== true) {
      console.error(
        CreatedUser?.data?.message?.message || "User creation failed"
      );
    }

    const newEmployeeUser = {
      role: ROLES.EMPLOYEE,
      userId: CreatedUser.data.uid,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      userName: formData.email,
      phoneNumber: phoneNational,
      timeZone: formData.timeZone,
      companyId: formData.companyId,
      status: true,
    };

    const create_user_res = await createEmployeeUser(newEmployeeUser);

    if (!create_user_res?.status) {
      const errMsg =
        typeof create_user_res?.error === "string"
          ? create_user_res.error
          : create_user_res?.error?.message || "Firestore write failed";
      console.error(errMsg);
      throw new Error(errMsg);
    }

    if (subscriptionId && subscriptionItemId) {
      void updateStripeQuantity(
        subscriptionId,
        subscriptionItemId,
        formData.companyId
      );
    }
    return { data: { ...create_user_res.data, status: true } };
  } catch (error) {
    throw error;
  }
}
// if (formData.employeerole === EMP_ROLES.CLIENT_TEAM) {
//   await axiosInstance.post(
//     `${BaseUrl}sendEmailData`,
//     {
//       email: NewUserData.email,
//       subject: "Your ProTeams account was successfully created",
//       contenttxt:
//         `<p>Hello ` +
//         NewUserData.firstname +
//         `,</p>
//         <p></p>
//     <p>You've been invited to the ProTeams mobile app. Please click on the
//     https://${DOMAINURL}/apps/ to download the app from iOS or Android stores.
//     Once you have installed the app you can you login by using the following details:</p>
//     <p>User name:<br />` +
//         NewUserData.email +
//         `</p>
//      <p>Password:<br />` +
//         NewUserData.password +
//         `</p>
//         <p></p>
//         <p></p>
//         <p>Proteams Support Team</p>
//         `,
//     },
//     {
//       headers: {
//         "Content-Type": "application/json",
//         withCredentials: true,
//         ...authHeaders,
//       },
//     }
//   );

//   await axiosInstance.post(
//     `${BaseUrl}sendSmsText`,
//     {
//       phoneNumber: [NewUserData.phoneNumber],
//       textMessage: `Hello ${NewUserData.firstname},\n\nYou're invited to the ProTeams App! Download it - https://${DOMAINURL}/apps/\n\nYour ID & password were sent to ${NewUserData.email}`,
//     },
//     {
//       headers: {
//         "Content-Type": "application/json",
//         withCredentials: true,
//         ...authHeaders,
//       },
//     }
//   );
// } else if (
//   formData.employeerole === EMP_ROLES.ADMIN ||
//   formData.employeerole === EMP_ROLES.GUEST ||
//   formData.employeerole === EMP_ROLES.SUPPORT
// ) {
//   await axiosInstance.post(
//     `${BaseUrl}sendEmailData`,
//     {
//       email: NewUserData.email,
//       subject: "Your ProTeams account was successfully created",
//       contenttxt:
//         `<p>Hello ` +
//         NewUserData.firstname +
//         `,</p>
//         <p></p>
//     <p>You have been invited to the ProTeams dashboard. To login and access your account please
//     use the following credentials:</p>
//     <p>Website: ${DOMAINURL}</p>
//     <p>Email Address: ` +
//         NewUserData.email +
//         `</p>
//      <p>Password :` +
//         NewUserData.password +
//         `</p>
//       <p>Role :` +
//         formData.employeerole +
//         `</p>
//         <p></p>
//         <p></p>
//         <p>The Proteams Support Team</p>
//         <p></p>
//         <p>*Note this app is best used on a desktop or laptop computer</p>`,
//     },
//     {
//       headers: {
//         "Content-Type": "application/json",
//         withCredentials: true,
//         ...authHeaders,
//       },
//     }
//   );

//   await axiosInstance.post(
//     `${BaseUrl}sendSmsText`,
//     {
//       phoneNumber: [NewUserData.phoneNumber],
//       textMessage: `Hello ${NewUserData.firstname},\n\nYou are invited to the ProTeams Dashboard!\n\nPlease check ${NewUserData.email} for the URL, your ID & password`,
//     },
//     {
//       headers: {
//         "Content-Type": "application/json",
//         withCredentials: true,
//         ...authHeaders,
//       },
//     }
//   );
// }

/**
 * Update Employee user data
 * @param {*} id : employee id
 * @param {*} formData : only pass data to be updated
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function updateEmployee(
  id,
  formData,
  editData,
  companyId,
  subscriptionId,
  subscriptionItemId,
  onSuccess,
  onError
) {
  if (id == null || id === "") {
    console.error("updateEmployee: missing user id");
    return onError?.({
      status: false,
      message: "Invalid employee id. Complete profile save or refresh and try again.",
    });
  }

  const currentUserId = getAuth().currentUser?.uid;
  delete formData.employeeId;
  delete formData.id;

  if (formData.phoneNumber != null && String(formData.phoneNumber).trim() !== "") {
    formData.phoneNumber = nationalPhoneForStorage(formData.phoneNumber);
  }

  let employeeData = {};
  try {
    const docRef = doc(firebase, COLLECTIONS.USERS, String(id));
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("No such document!");
    }
    employeeData = docSnap.data();
  } catch (error) {
    console.error("Error fetching employee data:", error);
    return onError?.({
      status: false,
      message: "Error fetching employee data",
    });
  }

  const fieldsToUpdate = Object.keys(formData)?.reduce((acc, key) => {
    if (formData[key] !== undefined && formData[key] !== editData?.[key]) {
      acc[key] = formData[key];
    }
    return acc;
  }, {});

  if (Object.keys(fieldsToUpdate)?.length > 0) {
    await updateUser(id, fieldsToUpdate);
  }

  // const oldStatus = employeeData.status;
  // const newStatus = formData.status;
  // if (oldStatus !== newStatus) {
  //   await updateStripeQuantity(subscriptionId, subscriptionItemId, companyId);
  // }

  let newEmail = null;
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

  try {
    if (formData?.email && employeeData?.email && employeeData?.email !== formData?.email) {
      // formData.userName = formData.email;
      const oldEmail = employeeData?.email;
      newEmail = formData?.email;
      const displayName = `${formData?.firstName || employeeData?.firstName} ${formData?.lastName || employeeData?.lastName}`;
      const userId = id;

      if (formData) formData.email = oldEmail;

      // await axiosInstance.post(`${BaseUrl}updateUserAuthEmail`,
      //   {
      //     userId: id,
      //     newEmail: NewUserData?.email,
      //   }, {
      //   headers: { ...defaultHeaders, ...authHeaders },
      // });
      // }

      if (formData?.employeerole === EMP_ROLES.CLIENT_TEAM) {
        await axiosInstance.post(`${BaseUrl}employeeEmailVerifyLink`, {
          oldEmail,
          newEmail,
          displayName,
          userId,
        }, { headers: { ...defaultHeaders, ...authHeaders } });
      } else {
        await axiosInstance.post(`${BaseUrl}emailVerifyLink`, {
          oldEmail,
          newEmail,
          displayName,
          userId,
        }, { headers: { ...defaultHeaders, ...authHeaders } });
      }
    }

    const docRef = doc(firebase, COLLECTIONS.USERS, String(id));
    await setDoc(
      docRef,
      {
        ...formData,
        ...(newEmail !== null ? { pendingEmail: newEmail } : {}),
        password: deleteField(),
        updatedAt: new Date().getTime(),
        updatedBy: currentUserId,
      },
      { merge: true }
    );

    const isClientTeam = formData?.employeerole === EMP_ROLES.CLIENT_TEAM;
    const notifyEmail = formData?.email || employeeData?.email;

    if (
      formData?.employeerole &&
      employeeData?.employeerole !== formData?.employeerole
    ) {
      Promise.resolve()
        .then(async () => {
          try {
            await axiosInstance.post(
              `${BaseUrl}sendManagedUserPasswordResetLink`,
              {
                email: notifyEmail,
                richAppInvite: isClientTeam,
                inviteFirstName:
                  formData?.firstName || employeeData?.firstName || "",
                contactName: "",
                companyName: "",
              },
              { headers: { ...defaultHeaders, ...authHeaders } }
            );
            if (companyId) {
              await incrementNotificationLimits(companyId);
            }
          } catch (err) {
            console.error("Employee role-change invite error:", err);
          }
        })
        .catch((err) => console.error("Employee role-change invite error:", err));
    }

    onSuccess?.({
      status: true,
      message: "Updated successfully",
      response: { status: "success" },
    });
  } catch (error) {
    console.error("Error updating employee:", error);
    onError?.({
      status: false,
      message: error?.message || "Error updating employee",
    });
  }
}

export async function updateEmployeeStatus(
  id,
  newStatus,
  oldStatus,
  companyId,
  subscriptionId = null,
  subscriptionItemId = null,
  onSuccess,
  onError
) {
  try {
    const currentUserId = getAuth().currentUser?.uid;

    const docRef = doc(firebase, COLLECTIONS.USERS, id);

    await setDoc(
      docRef,
      {
        status: newStatus,
        updatedAt: Date.now(),
        updatedBy: currentUserId,
      },
      { merge: true }
    );

    if (subscriptionId && subscriptionItemId && oldStatus !== newStatus) {
      await updateStripeQuantity(
        subscriptionId,
        subscriptionItemId,
        companyId
      );
    }

    const response = {
      status: true,
      message: "Employee status updated successfully",
    };

    onSuccess?.(response);
    return response;
  } catch (error) {
    console.error("updateEmployeeStatus error:", error);

    const response = {
      status: false,
      message: error?.message || "Something went wrong",
    };

    onError?.(response);
    return response;
  }
}

/**
 * Remove Employee user from system
 * First remove from firebase auth
 * then remove from users collection
 * @param {*} id
 * @param {*} empData
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function deleteEmployee(id, empData, subscriptionId = null, subscriptionItemId = null, onSuccess, onError) {
  const documentRef = doc(firebase, COLLECTIONS.USERS, id);

  try {
    const docSnapshot = await getDoc(documentRef);

    if (!docSnapshot.exists()) {
      onError?.({ status: false, error: "Document Not found" });
      return;
    }

    const userData = docSnapshot.data();
    const groupIds = userData?.group || [];
    const firstName = userData?.firstName;
    const email = userData?.email || "";
    const companyId = userData?.companyId || "";

    await Promise.all(groupIds?.map((groupId) => updateEmployeetoGroup("remove", groupId, id)));

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    const res = await axiosInstance.post(
      `${BaseUrl}removeUser`,
      { id, firstName, email },
      { headers: { ...defaultHeaders, ...authHeaders } }
    );

    if (!res.data.status) {
      throw new Error("Failed to delete user from Firebase Authentication");
    }

    await updateDoc(documentRef, {
      deleted: true,
      status: false,
      deletedAt: new Date().toISOString(),
    });

    if (subscriptionId && subscriptionItemId) {
      await updateStripeQuantity(subscriptionId, subscriptionItemId, companyId);
    }
    onSuccess?.({ status: true, message: "Delete successful", res });

    // setTimeout(async () => {
    //   try {
    //     if (!empData?.email) {
    //       console.warn("No email provided for background email");
    //       return;
    //     }
    //     console.log("res", empData)

    //     await axiosInstance.post(
    //       `${BaseUrl}sendEmailData`,
    //       {
    //         email: empData.email,
    //         subject: "A user was removed from the Proteams.io",
    //         contenttxt: `
    //           <div style="font-family: Arial, sans-serif; color: #333; padding: 20px; max-width: 600px; margin: auto; border: 1px solid #eee; border-radius: 10px;">
    //             <div style="text-align: center; margin-bottom: 20px;">
    //               <img src="https://${DOMAINURL}/csmGrayTextLogin.png" alt="ProTeams.io Logo" style="height: 60px;" />
    //             </div>
    //             <p>Hello,</p>
    //             <p>A user with an email address <strong>${empData?.email}</strong> has been deleted from the CSM app. Please contact management if this was an error.</p>
    //             <p style="font-size: 14px; color: #666;">
    //               If you have any questions about using the Web Portal or need assistance getting started, please don't hesitate to reach out to our support team.
    //             </p>
    //             <hr style="margin: 30px 0 0;">
    //             <div class="footer" style="text-align: center; color: #666; font-size: 13px;">
    //               <p>&copy; ${new Date().getFullYear()} ProTeams.io. All rights reserved.</p>
    //             </div>
    //           </div>
    //         `,
    //       },
    //       {
    //         headers: { "Content-Type": "application/json", ...authHeaders },
    //         withCredentials: true,
    //       }
    //     );

    //     console.log("Background email sent successfully to:", empData?.email);
    //   } catch (err) {
    //     console.error("Background email failed:", err);
    //   }
    // }, 0);
  } catch (error) {
    console.error("Error while deleting user:", error);
    onError?.({ status: false, error: error.message || "An error occurred" });
  }
}

/**
 * Returns summary data for employee collection
 * @param {*} formData : may be used for filter by different filed
 * @param {*} onSuccess
 * @param {*} onError
 */
// export async function getEmployeeSummary(formData, onSuccess, onError) {
//   firebase
//     .firestore()
//     .collection(COLLECTIONS.USERS)
//     .where("role", "==", ROLES.EMPLOYEE)
//     .limit(4)
//     .get()
//     .then((querySnapshot) => {
//       let results = [];
//       querySnapshot.forEach((doc) => {
//         let data = doc.data();
//         results.push({ id: doc.id, ...data });
//       });

//       onSuccess && onSuccess(results);
//     })
//     .catch((error) => {
//       console.log("Error getting documents: ", error);
//       onError && onError(error);
//     });
// }

/**
 * Returns summary data for employee collection
 * @param {*} formData : may be used for filter by different filed
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getEmployeeByFilter(formData, onSuccess, onError) {
  try {
    if (!formData?.clients && !formData?.groups) {
      onError && onError("Missing filter data");
      return;
    }

    let refUsers = query(
      collection(firebase, COLLECTIONS.USERS),
      or(
        and(where("companyId", "==", formData?.companyId), where("role", "==", "Employee")),
        and(where("companyId", "==", formData?.companyId), where("role", "array-contains", "Employee"))
      )
    );

    // if (formData.clients && formData.clients !== 'All') {
    //   refUsers = query(refUsers, where('clients', 'array-contains', formData?.clients?.id));
    // }

    if (formData?.groups && formData?.groups !== "All") {
      refUsers = query(
        refUsers,
        where("group", "array-contains", formData?.groups?.groupId)
      );
    }

    const snapshot = await getDocs(refUsers);
    let employees = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    if (formData?.clients && formData?.clients !== "All") {
      employees = employees?.filter((emp) => {
        if (!Array.isArray(emp.clients)) return false;
        return emp?.clients?.some(
          (client) => client?.clientid === formData?.clients?.id
        );
      });
    }

    onSuccess && onSuccess(employees);
  } catch (error) {
    console.error("Error getting documents: ", error);
    onError && onError(error);
  }
}

export const setAnyUserData = async (userId, data) => {
  const userRef = doc(firebase, "users", userId);
  await updateDoc(userRef, data);
  return { status: true, message: "User information updated successfully." };
};

export const setUserInformation = async (userId, data, mobileChange) => {

  if (mobileChange && data?.phoneNumber) {
    const phoneQuery = query(
      collection(firebase, "users"),
      where("phoneNumber", "==", data?.phoneNumber),
      where("status", "==", true),
      limit(1)
    );

    const phoneSnapshot = await getDocs(phoneQuery);

    if (!phoneSnapshot.empty) {
      const existingUser = phoneSnapshot.docs[0].data();
      if (existingUser?.id !== userId) {
        return {
          status: false,
          field: "phone",
          message: "This phone number is already in use by another account.",
        };
      }
    }
  }

  const userRef = doc(firebase, "users", userId);
  await updateDoc(userRef, data);
  return { status: true, message: "User information updated successfully." };
};

export const setUserInfoHistory = async (email, data) => {
  try {
    const emailQuery = query(
      collection(firebase, "user_history"),
      where("email", "==", email),
      limit(1)
    );

    const emailSnapshot = await getDocs(emailQuery);

    if (!emailSnapshot.empty) {
      const existingDoc = emailSnapshot.docs[0];
      const userRef = doc(firebase, "user_history", existingDoc.id);
      await updateDoc(userRef, data);
      return { status: true, message: "User information updated successfully." };
    } else {
      await addDoc(collection(firebase, "user_history"), {
        email,
        ...data,
        createdAt: new Date(),
      });
      return { status: true, message: "New user record created successfully." };
    }
  } catch (error) {
    console.error("Error updating user info history:", error);
    return { status: false, message: "Failed to update or create user record.", error };
  }
};


export async function UpdateUserStatus(id, status) {
  try {
    const auth = getAuth();
    const currentUserId = auth.currentUser?.uid;
    const userRef = doc(firebase, COLLECTIONS.USERS, id);

    if (status === false) {
      await updateDoc(userRef, {
        status: false,
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: currentUserId,
      });
    } else {
      await updateDoc(userRef, {
        status: true,
        deleted: deleteField(),
        deletedAt: deleteField(),
        deletedBy: deleteField(),
      });
    }
  } catch (error) {
    console.error("Error updating user status:", error);
  }
}

export async function UpdateUserRole(id, formData, companyId) {
  try {
    const auth = getAuth();
    const currentUserId = auth.currentUser?.uid;

    const docRef = doc(firebase, COLLECTIONS.USERS, id);

    await setDoc(
      docRef,
      {
        employeerole: formData?.employeerole,
        updatedAt: new Date().getTime(),
        updatedBy: currentUserId,
      },
      { merge: true }
    );

    const isClientTeam = formData?.employeerole === EMP_ROLES.CLIENT_TEAM;

    if (formData?.employeerole) {
      const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
      Promise.resolve()
        .then(async () => {
          try {
            await axiosInstance.post(
              `${BaseUrl}sendManagedUserPasswordResetLink`,
              {
                email: formData?.email,
                richAppInvite: isClientTeam,
                inviteFirstName:
                  formData?.firstname || formData?.firstName || "",
                contactName: "",
                companyName: "",
              },
              { headers: { ...defaultHeaders, ...authHeaders } }
            );
            if (companyId) {
              await incrementNotificationLimits(companyId);
            }
          } catch (err) {
            console.error("UpdateUserRole invite error:", err);
          }
        })
        .catch((err) => console.error("UpdateUserRole invite error:", err));
    }
  } catch (error) {
    console.error("Failed to update user role:", error);
    throw error;
  }
}

export async function deleteUsers(id, subscriptionId = null, subscriptionItemId = null, onSuccess, onError) {
  const documentRef = doc(firebase, COLLECTIONS.USERS, id);

  try {
    const docSnapshot = await getDoc(documentRef);

    if (!docSnapshot.exists()) {
      onError?.({ status: false, error: "Document Not found" });
      return;
    }

    const userData = docSnapshot.data();
    const userRole = userData?.role;
    const groupIds = userData?.group || [];
    const firstName = userData?.firstName;
    const email = userData?.email || "";
    const companyId = userData?.companyId || "";

    await Promise.all(groupIds?.map((groupId) => updateEmployeetoGroup("remove", groupId, id)));

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(
      `${BaseUrl}removeUser`,
      { id, firstName, email },
      { headers: { ...defaultHeaders, ...authHeaders } }
    );

    if (!res.data.status) {
      throw new Error("Failed to delete user from Firebase Authentication");
    }

    await updateDoc(documentRef, {
      deleted: true,
      status: false,
      deletedAt: new Date().toISOString(),
    });

    if (subscriptionId && subscriptionItemId) {
      await updateStripeQuantity(subscriptionId, subscriptionItemId, companyId);
    }
    onSuccess?.({ status: true, message: "Delete successful", res });

    // setTimeout(async () => {
    //   try {
    //     if (empData?.email) {
    //       await axiosInstance.post(
    //         `${BaseUrl}sendEmailData`,
    //         {
    //           email: empData?.email,
    //           subject: "A user was removed from the Proteams.io",
    //           contenttxt: `
    //             <div style="font-family: Arial, sans-serif; color: #333; padding: 20px; max-width: 600px; margin: auto; border: 1px solid #eee; border-radius: 10px;">
    //               <div style="text-align: center; margin-bottom: 20px;">
    //                 <img src="https://${DOMAINURL}/csmGrayTextLogin.png" alt="ProTeams.io Logo" style="height: 60px;" />
    //               </div>
    //               <p>Hello,</p>
    //               <p>A user with an email address <strong>${empData?.email}</strong> has been deleted from the CSM app. Please contact management if this was an error.</p>
    //               <p style="font-size: 14px; color: #666;">
    //                 If you have any questions about using the Web Portal or need assistance getting started, please don't hesitate to reach out to our support team.
    //               </p>
    //               <hr style="margin: 30px 0 0;">
    //               <div class="footer" style="text-align: center; color: #666; font-size: 13px;">
    //                 <p>&copy; ${new Date().getFullYear()} ProTeams.io. All rights reserved.</p>
    //               </div>
    //             </div>
    //           `,
    //         },
    //         {
    //           headers: { "Content-Type": "application/json", ...authHeaders },
    //           withCredentials: true,
    //         }
    //       );
    //       console.log("Background email sent successfully to:", empData.email);
    //     }
    //   } catch (err) {
    //     console.error("Background email failed:", err);
    //   }
    // }, 0);

    if (userRole === "Client" && userData?.client) {
      const clientRef = doc(firebase, COLLECTIONS.CLIENT, userData?.client);
      Promise.resolve().then(async () => {
        try {
          const clientSnap = await getDoc(clientRef);
          if (!clientSnap.exists()) return;

          const clientData = clientSnap.data();
          const updatedContacts = (clientData?.contact || [])?.map((contact) =>
            contact?.email === userData?.email
              ? { ...contact, invite: false }
              : contact
          );

          await updateDoc(clientRef, { contact: updatedContacts });
          console.log("Client contact invite status updated in background.");
        } catch (err) {
          console.error("Failed to update client contact invite:", err);
        }
      });
    }
  } catch (error) {
    console.error("Error while deleting user:", error);
    onError?.({ status: false, error: error.message || "An error occurred" });
  }
}

export const incrementNotificationLimits = async (companyId) => {
  if (!companyId) return;
  try {
    const limitsRef = collection(firebase, "notification_limits");
    const q = query(limitsRef, where("companyId", "==", companyId));
    const snapshot = await getDocs(q);
    const activeDoc = snapshot.docs.find((d) => !d.data().deleted);

    if (activeDoc) {
      await updateDoc(doc(firebase, "notification_limits", activeDoc.id), {
        notificationCount: increment(1),
        smsRemainingAllowance: increment(1),
        lastUpdatedBy: companyId,
        updatedAt: serverTimestamp(),
      });
      console.log("Firestore notification limits updated");
    } else {
      console.log("No active notification limits found for this company");
    }
  } catch (err) {
    console.error("Error updating notification limits:", err);
  }
};
